import { ReactNode } from 'react';
import { Box, Card, CardContent, Stack, Typography } from '@mui/material';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import { neutral } from '../../theme/colors';

interface ChartCardProps {
	title: string;
	/** One line saying how to read the chart, or what the number means. */
	subtitle?: string;
	action?: ReactNode;
	/** Rendered instead of children when there is nothing to plot. */
	isEmpty?: boolean;
	emptyMessage?: string;
	children: ReactNode;
	sx?: any;
}

export const ChartCard = ({
	title,
	subtitle,
	action,
	isEmpty,
	emptyMessage = 'No data for the selected period.',
	children,
	sx,
}: ChartCardProps) => (
	<Card
		elevation={0}
		sx={{
			height: '100%',
			border: `1px solid ${neutral[200]}`,
			borderRadius: 2,
			display: 'flex',
			flexDirection: 'column',
			...sx,
		}}
	>
		<CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column', '&:last-child': { pb: 3 } }}>
			<Stack
				direction="row"
				alignItems="flex-start"
				justifyContent="space-between"
				spacing={2}
				sx={{ mb: subtitle ? 2.5 : 2 }}
			>
				<Box>
					<Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
						{title}
					</Typography>
					{subtitle && (
						<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
							{subtitle}
						</Typography>
					)}
				</Box>
				{action}
			</Stack>
			<Box sx={{ flexGrow: 1, minWidth: 0 }}>
				{isEmpty ? <EmptyState message={emptyMessage} /> : children}
			</Box>
		</CardContent>
	</Card>
);

export const EmptyState = ({ message, height = 200 }: { message: string; height?: number }) => (
	<Stack
		alignItems="center"
		justifyContent="center"
		spacing={1.5}
		sx={{
			minHeight: height,
			height: '100%',
			borderRadius: 2,
			border: `1px dashed ${neutral[200]}`,
			backgroundColor: neutral[50],
			px: 3,
			py: 4,
		}}
	>
		<InsightsOutlinedIcon sx={{ color: neutral[300], fontSize: 32 }} />
		<Typography variant="body2" color="text.secondary" align="center">
			{message}
		</Typography>
	</Stack>
);

export const SectionHeading = ({ title, description }: { title: string; description?: string }) => (
	<Box sx={{ mb: 3 }}>
		<Typography variant="h6" sx={{ fontWeight: 600 }}>
			{title}
		</Typography>
		{description && (
			<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
				{description}
			</Typography>
		)}
	</Box>
);
